import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

import { reportBootStage } from 'src/utils/boot/boot-stage';
import { withTimeout, TimeoutError } from 'src/utils/promise-timeout';
import { capacitorUpdaterService } from 'src/utils/Services/capacitor-updater.service';

import { initializeAuthListener } from 'src/auth/store/firebase/auth-store';

import { useSnackbar } from 'src/components/snackbar';

// ----------------------------------------------------------------------

const TIMEOUTS = {
  NOTIFY_APP_READY: 25000,
  GET_CURRENT_BUNDLE: 5000,
  VERSION_CHECK: 5000,
  RESET_TO_BUILTIN: 15000,
  ABSOLUTE_MAX: 35000,
  RESET_RELOAD_FALLBACK: 8000,
} as const;

export type UpdaterNotification = {
  message: string;
  variant: 'warning' | 'error';
};

export type UpdaterInitializationResult = {
  ready: boolean;
  notification?: UpdaterNotification;
};

let updaterInitializationPromise: Promise<UpdaterInitializationResult> | null = null;
let updaterNotificationConsumed = false;

async function recordRollbackFailure(error: unknown): Promise<void> {
  if (!(error instanceof Error)) return;
  const errorMessage = error.message.toLowerCase();
  const isRollbackError =
    errorMessage.includes('rollback') ||
    errorMessage.includes('update failed') ||
    errorMessage.includes('bundle failed');
  if (!isRollbackError) return;

  try {
    const currentBundle = await withTimeout(
      CapacitorUpdater.current(),
      TIMEOUTS.GET_CURRENT_BUNDLE,
      'getCurrentBundle timeout in error handler'
    );
    localStorage.setItem('capacitor-updater-failed-bundle', JSON.stringify(currentBundle));
  } catch (bundleError) {
    console.error('Failed to store failed bundle status:', bundleError);
  }
}

async function runUpdaterInitialization(): Promise<UpdaterInitializationResult> {
  if (!Capacitor.isNativePlatform()) return { ready: true };

  // Auth restoration is independent from OTA network work and is idempotent.
  initializeAuthListener();

  await new Promise((resolve) => setTimeout(resolve, 100));
  await withTimeout(
    CapacitorUpdater.notifyAppReady(),
    TIMEOUTS.NOTIFY_APP_READY,
    'notifyAppReady timeout'
  );

  await withTimeout(
    CapacitorUpdater.current(),
    TIMEOUTS.GET_CURRENT_BUNDLE,
    'getCurrentBundle timeout'
  );

  const versionCheck = await withTimeout(
    capacitorUpdaterService.shouldUseNativeVersion(),
    TIMEOUTS.VERSION_CHECK,
    'versionCheck timeout'
  );

  console.log('Updater version status:', {
    nativeVersion: versionCheck.nativeVersion,
    bundleVersion: versionCheck.bundleVersion,
    isRunningNative: versionCheck.isRunningNative,
    shouldReset: versionCheck.shouldReset,
  });

  if (versionCheck.shouldReset) {
    await withTimeout(
      capacitorUpdaterService.resetToBuiltin(),
      TIMEOUTS.RESET_TO_BUILTIN,
      'resetToBuiltin timeout'
    );
    return { ready: false };
  }

  const failedBundle = localStorage.getItem('capacitor-updater-failed-bundle');
  if (failedBundle) {
    localStorage.removeItem('capacitor-updater-failed-bundle');
    return {
      ready: true,
      notification: {
        message: 'Previous update was rolled back due to startup issues',
        variant: 'warning',
      },
    };
  }

  return { ready: true };
}

async function handleUpdaterInitialization(): Promise<UpdaterInitializationResult> {
  try {
    return await withTimeout(
      runUpdaterInitialization(),
      TIMEOUTS.ABSOLUTE_MAX,
      'Updater initialization exceeded the absolute startup limit'
    );
  } catch (error) {
    console.error('Failed to initialize Capacitor Updater:', error);
    await recordRollbackFailure(error);

    if (error instanceof TimeoutError) {
      return {
        ready: true,
        notification: {
          message: 'App updater initialization timed out. Loading app anyway.',
          variant: 'warning',
        },
      };
    }

    return {
      ready: true,
      notification: {
        message: 'App updater initialization failed. Updates may not work properly.',
        variant: 'error',
      },
    };
  }
}

export function initializeUpdaterOnce(): Promise<UpdaterInitializationResult> {
  if (!updaterInitializationPromise) {
    updaterInitializationPromise = handleUpdaterInitialization();
  }
  return updaterInitializationPromise;
}

/**
 * Returns the memoized initialization's user-facing notification at most once
 * per JS process. Shares the consumed flag with the guard component below, so
 * the boot controller's updater gate and the legacy guard can coexist during
 * rollout without a double toast.
 */
export function takeUpdaterNotification(
  result: UpdaterInitializationResult
): UpdaterNotification | null {
  if (!result.notification || updaterNotificationConsumed) return null;
  updaterNotificationConsumed = true;
  return result.notification;
}

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function AppUpdaterGuard({ children }: Props) {
  const [isReady, setIsReady] = useState(!Capacitor.isNativePlatform());
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    reportBootStage('updater');
  }, []);

  useEffect(() => {
    let mounted = true;
    let reloadFallback: ReturnType<typeof setTimeout> | undefined;

    initializeUpdaterOnce().then((result) => {
      if (result.notification && !updaterNotificationConsumed) {
        updaterNotificationConsumed = true;
        enqueueSnackbar(result.notification.message, {
          variant: result.notification.variant,
        });
      }
      if (mounted) setIsReady(result.ready);
      if (mounted && !result.ready) {
        // ready:false means resetToBuiltin() ran and a native reload should land
        // within ~2s. If it never arrives this state is a dead end (the init
        // result is memoized for the process lifetime), so reload ourselves.
        reloadFallback = setTimeout(() => {
          window.location.reload();
        }, TIMEOUTS.RESET_RELOAD_FALLBACK);
      }
    });

    return () => {
      mounted = false;
      if (reloadFallback) clearTimeout(reloadFallback);
    };
  }, [enqueueSnackbar]);

  if (!isReady && Capacitor.isNativePlatform()) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Initializing app...</div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            border: 0,
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600,
            backgroundColor: '#5BE49B',
            color: '#004B50',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
